import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { pickWorldPoint } from '@viewer/lib/picking'
import type { WorkPlane } from '@viewer/store/measureSlice'

// Renders the work plane as a translucent amber square + grid in the
// scene whenever the slice has a workPlane set. Also implements the
// face-pick mode: while workPlanePickMode is true the next click on a
// model surface sets workPlane to the face's plane (origin + world
// normal) and exits pick mode.
//
// The plane mesh is sized to the union bbox of all visible models,
// padded ~50% on each side, so it visibly covers the working area
// without being so large it kills depth cues.

export function useWorkPlane(): void {
  const groupRef = useRef<THREE.Group | null>(null)

  // ---- Render / re-render the plane visualization ----
  useEffect(() => {
    const apply = () => {
      const world = viewerApi.getWorld()
      if (!world) {
        teardown()
        return
      }
      const plane = useAppStore.getState().workPlane
      const visible = useAppStore.getState().workPlaneVisible
      if (!plane || !visible) {
        teardown()
        return
      }
      // Build the group once, reuse on plane changes by replacing
      // the children rather than churning the scene attachment.
      if (!groupRef.current) {
        const g = new THREE.Group()
        g.name = 'vn2r-work-plane'
        g.renderOrder = 999 // draw on top of regular geometry
        world.scene.three.add(g)
        groupRef.current = g
      }
      const group = groupRef.current
      while (group.children.length > 0) {
        const child = group.children[0]
        group.remove(child)
        disposeObject(child)
      }

      const sceneSize = computeSceneSize()
      const planeSize = Math.max(sceneSize, 5) * 1.5

      const planeGeom = new THREE.PlaneGeometry(planeSize, planeSize, 1, 1)
      const planeMat = new THREE.MeshBasicMaterial({
        color: 0xf59e0b,
        transparent: true,
        opacity: 0.18,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
      const planeMesh = new THREE.Mesh(planeGeom, planeMat)
      planeMesh.frustumCulled = false
      planeMesh.renderOrder = 999

      // Grid overlay aligned with the plane. THREE.GridHelper is
      // always XZ-oriented so we transform-then-attach.
      const cells = Math.max(8, Math.round(planeSize))
      const grid = new THREE.GridHelper(planeSize, cells, 0xf59e0b, 0xf59e0b)
      const gridMat = grid.material as THREE.LineBasicMaterial
      gridMat.transparent = true
      gridMat.opacity = 0.85
      gridMat.depthWrite = false
      gridMat.depthTest = false
      grid.renderOrder = 1000
      // Rotate from XZ to XY so it lives in the plane's local frame
      // (PlaneGeometry's normal is +Z in local space).
      grid.rotation.x = Math.PI / 2

      // Solid border so the user can spot the plane edge.
      const borderGeom = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-planeSize / 2, -planeSize / 2, 0),
        new THREE.Vector3(planeSize / 2, -planeSize / 2, 0),
        new THREE.Vector3(planeSize / 2, planeSize / 2, 0),
        new THREE.Vector3(-planeSize / 2, planeSize / 2, 0),
        new THREE.Vector3(-planeSize / 2, -planeSize / 2, 0),
      ])
      const borderMat = new THREE.LineBasicMaterial({
        color: 0xf59e0b,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 1,
      })
      const border = new THREE.Line(borderGeom, borderMat)
      border.renderOrder = 1001

      group.add(planeMesh)
      group.add(grid)
      group.add(border)

      orientGroupToPlane(group, plane)
    }

    apply()
    const unsubscribe = useAppStore.subscribe((state, prev) => {
      if (
        state.workPlane === prev.workPlane &&
        state.workPlaneVisible === prev.workPlaneVisible
      ) {
        return
      }
      apply()
    })
    const unsubViewer = viewerApi.subscribe(apply)
    return () => {
      unsubscribe()
      unsubViewer()
      teardown()
    }

    function teardown() {
      const g = groupRef.current
      if (!g) return
      g.parent?.remove(g)
      while (g.children.length > 0) {
        const child = g.children[0]
        g.remove(child)
        disposeObject(child)
      }
      groupRef.current = null
    }
  }, [])

  // ---- Face-pick mode ----
  //
  // While workPlanePickMode is true, the next click on a model
  // surface sets the work plane to that face. Uses our own raycaster
  // because useInteraction also early-returns when measureMode is on,
  // but face-pick should be independent from measureMode.
  useEffect(() => {
    let cleanup: (() => void) | null = null
    const setup = () => {
      cleanup?.()
      cleanup = null
      const world = viewerApi.getWorld()
      if (!world) return
      const dom = world.renderer?.three.domElement
      if (!dom) return

      const onClick = async (e: PointerEvent) => {
        if (!useAppStore.getState().workPlanePickMode) return
        if (e.button !== 0) return
        e.preventDefault()
        e.stopPropagation()
        // Use Fragments' raycast (which returns a normalised world
        // normal) instead of THREE.intersectObjects, which crashes
        // on Fragments' streamed BufferAttributes.
        const result = await pickWorldPoint(
          world,
          dom,
          e.clientX,
          e.clientY,
          { snap: false, fallbackToGround: false },
        ).catch(() => null)
        if (!result || !result.normal) return
        useAppStore.getState().setWorkPlane({
          kind: 'face',
          origin: [result.point.x, result.point.y, result.point.z],
          normal: [result.normal.x, result.normal.y, result.normal.z],
        })
      }
      // Capture phase so we run BEFORE useInteraction's pointerdown
      // (which would start a marquee on empty drags).
      dom.addEventListener('pointerdown', onClick, { capture: true })
      cleanup = () => {
        dom.removeEventListener('pointerdown', onClick, { capture: true })
      }
    }
    setup()
    const unsubscribe = viewerApi.subscribe(setup)
    return () => {
      unsubscribe()
      cleanup?.()
    }
  }, [])

  // Esc cancels face-pick mode (separate from useMeasure's Esc which
  // closes measureMode; both fire and each is a no-op when off).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const s = useAppStore.getState()
      if (s.workPlanePickMode) s.setWorkPlanePickMode(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

function computeSceneSize(): number {
  const box = new THREE.Box3()
  let initialised = false
  for (const [, model] of viewerApi.iterModels()) {
    const mb = model.box
    if (!mb || mb.isEmpty()) continue
    if (!initialised) {
      box.copy(mb)
      initialised = true
    } else {
      box.union(mb)
    }
  }
  if (!initialised) return 10
  const size = new THREE.Vector3()
  box.getSize(size)
  return Math.max(size.x, size.y, size.z)
}

function orientGroupToPlane(group: THREE.Group, plane: WorkPlane) {
  group.position.set(plane.origin[0], plane.origin[1], plane.origin[2])
  // PlaneGeometry's local normal is +Z. Build a quaternion that
  // rotates +Z into the desired world normal.
  const target = new THREE.Vector3(
    plane.normal[0],
    plane.normal[1],
    plane.normal[2],
  ).normalize()
  const planeNormal = new THREE.Vector3(0, 0, 1)
  const quat = new THREE.Quaternion().setFromUnitVectors(planeNormal, target)
  group.quaternion.copy(quat)
}

function disposeObject(obj: THREE.Object3D) {
  obj.traverse((o) => {
    const m = o as THREE.Mesh & THREE.GridHelper
    if (m.geometry) m.geometry.dispose()
    const mat = (m as THREE.Mesh).material
    if (Array.isArray(mat)) {
      for (const x of mat) x.dispose()
    } else if (mat) {
      ;(mat as THREE.Material).dispose()
    }
  })
}
